import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider, Theme } from '@mui/material/styles';
import { getThemeByType, BrandTheme } from '../config/themes';

interface ThemeContextType {
  currentBrand: BrandTheme;
  themeType: string;
  setThemeType: (type: string) => void;
  theme: Theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useCustomTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useCustomTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const CustomThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeType, setThemeTypeState] = useState<string>(() => {
    // Check URL parameter first
    const urlParams = new URLSearchParams(window.location.search);
    const urlType = urlParams.get('type');

    if (urlType) {
      localStorage.setItem('themeType', urlType.toLowerCase());
      return urlType.toLowerCase();
    }

    // Then check localStorage
    return localStorage.getItem('themeType') || 'bellaitaliana';
  });

  const currentBrand = useMemo(() => getThemeByType(themeType), [themeType]);
  const theme = useMemo(() => createTheme(currentBrand.theme), [currentBrand]);

  const setThemeType = (type: string) => {
    const normalizedType = type.toLowerCase();
    setThemeTypeState(normalizedType);
    localStorage.setItem('themeType', normalizedType);
  };

  useEffect(() => {
    // Set document title based on brand
    document.title = `${currentBrand.name} POS`;
  }, [currentBrand]);

  // Listen for URL changes to update theme
  useEffect(() => {
    const handleUrlChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const urlType = urlParams.get('type');

      if (urlType && urlType.toLowerCase() !== themeType) {
        setThemeType(urlType);
      }
    };

    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, [themeType]);

  const contextValue: ThemeContextType = {
    currentBrand,
    themeType,
    setThemeType,
    theme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};